import { Component, OnInit } from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';

/**
 * Create/edit dialog for misc_event entities, a studio's occurrence of a
 * franchise-wide miscellaneous event (e.g. one Dance-O-Rama edition).
 *
 * Packages and deductions reference the event so the Miscellaneous Report can
 * scope rows and costs to one edition regardless of week, payment date or how
 * many component packages/enrollments the event accumulates.
 */
@Component({
  selector: 'app-misc-event-entity',
  templateUrl: './misc-event-entity.component.html',
  styleUrls: ['./misc-event-entity.component.css']
})
export class MiscEventEntityComponent extends BaseDialogComponent implements OnInit {

  override eckType = "misc_event";
  override bundle = "misc_event";
  override entityId = this._injectedDialogData['data']['EntityID'];

  miscEventFormGroup;

  /**
   * Franchise-wide event, visible to every studio. Offered only to super
   * admins; the server enforces the same rule (permission-gated endpoint plus
   * storage-level guards), so hiding the checkbox is UX, not the boundary.
   */
  isSystemWide = false;

  /**
   * The owl range picker binds a single [start, end] value, while the form
   * keeps the two dates as separate required controls (the payload, the
   * validity gate and the edit-mode prefill all address them individually).
   * This holds the picker's view of that pair; eventDateRangeChange() writes
   * the selection back into the two controls.
   */
  eventDateRange: Date[] = [];

  /** Inline delete confirmation state (edit mode only). */
  showDeleteConfirm = false;
  isDeleting = false;

  get canCreateSystemWide(): boolean {
    return this._permissionsService.isSuperAdmin();
  }

  get isEditMode(): boolean {
    return this.initActionType === 'edit';
  }

  override ngOnInit(): void {
    this.initActionType = this._injectedDialogData['data']['action'];
    this.actionType = this._injectedDialogData['data']['action'];

    // Edit mode is seeded from the already-parsed event row the report holds
    // (id/title/packageCode/dateStart/dateEnd/systemWide) rather than from the
    // studio-scoped ECK wrapper, which cannot see system-wide (studio-less)
    // events at all.
    const fieldsData = this._injectedDialogData['data']?.fieldsData ?? {};
    const editing = this.initActionType === 'edit';

    if (editing) {
      this.entityId = fieldsData?.id ?? this.entityId;
      this.isSystemWide = !!fieldsData?.systemWide;
    }

    this.miscEventFormGroup = this._formBuilder.group({
      title: [editing ? (fieldsData?.title ?? null) : null, Validators.required],
      // Default to the package code already selected on the report filter.
      field_package_code: [
        (editing ? fieldsData?.packageCode : fieldsData?.field_package_code) || null,
        Validators.required,
      ],
      // Dates are stored on the form as moments; onSubmitMiscEvent formats
      // them to 'YYYY-MM-DD' for the API.
      field_event_date_start: [editing && fieldsData?.dateStart ? moment(fieldsData.dateStart, 'YYYY-MM-DD') : null, Validators.required],
      field_event_date_end: [editing && fieldsData?.dateEnd ? moment(fieldsData.dateEnd, 'YYYY-MM-DD') : null, Validators.required],
    });

    // Seed the owl picker so edit mode opens showing the saved range. The
    // picker runs on the native adapter (OwlNativeDateTimeModule in
    // app.module), so it wants Dates at local midnight. Parsing the
    // 'YYYY-MM-DD' string through moment and taking toDate() keeps the
    // calendar day intact regardless of timezone.
    if (editing && fieldsData?.dateStart && fieldsData?.dateEnd) {
      this.eventDateRange = [
        moment(fieldsData.dateStart, 'YYYY-MM-DD').toDate(),
        moment(fieldsData.dateEnd, 'YYYY-MM-DD').toDate(),
      ];
    }
  }

  /**
   * Mirror an owl range selection into the two reactive controls. The event
   * value is a [start, end] Date array (or empty when cleared); clearing must
   * null both controls so the required validators re-block the Save button.
   */
  eventDateRangeChange(event: any): void {
    const range = Array.isArray(event?.value) ? event.value : [];

    if (range[0] && range[1]) {
      this.miscEventFormGroup.patchValue({
        field_event_date_start: moment(range[0]),
        field_event_date_end: moment(range[1]),
      });
    } else {
      this.miscEventFormGroup.patchValue({
        field_event_date_start: null,
        field_event_date_end: null,
      });
    }

    this.miscEventFormGroup.get('field_event_date_start').markAsTouched();
    this.miscEventFormGroup.get('field_event_date_end').markAsTouched();
  }

  /**
   * The base implementation loads the entity through the studio-scoped ECK
   * wrapper for 'edit' and patches arbitrary fieldsData into the NgForm. Both
   * are wrong here: the form is reactive and already prefilled from the parsed
   * event row, and system-wide events are invisible to the wrapper.
   */
  override ngAfterContentInit(): void {
  }

  onSubmitMiscEvent(f: NgForm) {
    this.displayProgressSpinner(true);

    // Convert the date-picker moments/Dates to the storage format.
    const values = { ...this.miscEventFormGroup.value };
    for (const key of ['field_event_date_start', 'field_event_date_end']) {
      if (values[key]) {
        values[key] = moment(values[key]).format('YYYY-MM-DD');
      }
    }

    if (this.isEditMode) {
      // Only the four mutable fields go up; field_studio_reference is
      // immutable server-side and the endpoint ignores it either way.
      this._drupalRESTService.httpPATCH(`/api/misc-event/${this.entityId}`, values)
        .subscribe(
          data => {
            this.displayProgressSpinner(false);
            this.closeDialog(data);
          },
          error => this.handleError(error)
        );
      return;
    }

    // System-wide events go through the dedicated endpoint: the wrapper path
    // cannot create them because a studio reference is injected into every
    // POST body, and the endpoint ignores studio fields by design.
    const request = this.isSystemWide
      ? this._drupalRESTService.httpPOST('/api/misc-event/system', values)
      : this._entityRESTService.createEntity(this.eckType, this.bundle, values);

    request.subscribe(
      data => {
        this.displayProgressSpinner(false);
        this.closeDialog(data);
      },
      error => this.handleError(error)
    );
  }

  startDeleteMiscEvent(): void {
    this.errorMessage = '';
    this.showDeleteConfirm = true;
  }

  cancelDeleteMiscEvent(): void {
    this.showDeleteConfirm = false;
  }

  /**
   * The endpoint refuses (409) while packages or deductions still reference
   * the event; surface those counts rather than a generic failure so staff
   * know what to unlink first.
   */
  confirmDeleteMiscEvent(): void {
    if (this.isDeleting) { return; }
    this.isDeleting = true;
    this.errorMessage = '';
    this.displayProgressSpinner(true);

    this._drupalRESTService.httpDELETE(`/api/misc-event/${this.entityId}`)
      .subscribe(
        () => {
          this.isDeleting = false;
          this.displayProgressSpinner(false);
          this._snackBar.open('Event deleted.', 'OK', { duration: 3000 });
          this.closeDialog({ deleted: true, id: this.entityId });
        },
        error => {
          this.isDeleting = false;
          this.displayProgressSpinner(false);
          this.showDeleteConfirm = false;

          const body = error?.error;
          if (error?.status === 409 && body?.error === 'in_use') {
            const packages = Number(body?.linked_packages ?? 0);
            const deductions = Number(body?.linked_deductions ?? 0);
            const message = `This event can't be deleted: ${packages} package${packages === 1 ? '' : 's'}`
              + ` and ${deductions} deduction${deductions === 1 ? '' : 's'} still reference it.`;
            this.errorMessage = message;
            this._snackBar.open(message, 'OK', { duration: 8000 });
            return;
          }

          this.handleError(error);
        }
      );
  }
}
